import {
  AuthorizeCardsReq,
  CancelCardOnFileLinkReq,
  CompleteCardSaveReq,
  CreateCardOnFileLinkReq,
  GetCardLinkStatusReq,
  type HelcimCard,
  HelcimClient,
  type HelcimProfile,
  LinkHelcimProfileReq,
  ListCardsOnFileReq,
  ProfileLinkState,
  RemoveHelcimCardReq,
  ResendCardOnFileLinkReq,
  SearchHelcimProfilesReq,
  UnlinkHelcimProfileReq,
  User,
  UserClient,
} from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CreditCard,
  History,
  Loader2,
  Mail,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  Unlink,
} from 'lucide-react';
import { type FC, useEffect, useId, useMemo, useRef, useState } from 'react';

import { CardOnFileAuditLog } from './CardOnFileAuditLog';
import { cardStatusLabel, formatBrandLabel, pickDisplayCard } from './cardOnFileDisplay';
import { helcimCardLinkQueryKey, helcimCardsOnFileQueryKey } from './cardOnFileQueryKey';
import { HelcimCardTokenizePanel } from './HelcimCardTokenizePanel';
import { HELCIM_PAY_IFRAME_ID } from './helcimPayTeardown';

// card_authorization_basis_type ids (kalos-core migration 397).
const BASIS_CONFIRMATION_EMAIL = 2;
const BASIS_RECORDED_LINE = 3;

// Record<ModalMode, string> so adding a mode without a title is a type error
// instead of a silent "Cards on file" fallback.
const MODAL_TITLES: Record<ModalMode, string> = {
  manage: 'Cards on file',
  search: 'Find card on file',
  capture: 'Add card on file',
  sendConfirm: 'Send card link',
  enterNow: 'Add new card',
  consent: 'How did the customer consent?',
  attest: 'Which cards did the customer ask us to use?',
};

type ModalMode =
  | 'manage'
  | 'search'
  | 'capture'
  | 'sendConfirm'
  | 'enterNow'
  | 'consent'
  | 'attest';

export interface CardOnFileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: number;
  grpcBaseUrl: string;
  /** Prefills the send-link recipient; the dialog always confirms it before sending. */
  customerEmail?: string;
  onTokenizeComplete?: () => void;
  /** Called with the selected card token after the user picks a card and taps **Change card**. */
  onChangeCard?: (tokenId: string) => Promise<void>;
  /**
   * Classes that make the dialog take the whole screen (the dashboard's phone and tablet layout).
   * When set, the corner X gives way to a worded Close at the top.
   */
  fullScreenClassName?: string;
}

export const CardOnFileModal: FC<CardOnFileModalProps> = ({
  open,
  onOpenChange,
  userId,
  grpcBaseUrl,
  customerEmail = '',
  onTokenizeComplete,
  onChangeCard,
  fullScreenClassName,
}) => {
  const queryClient = useQueryClient();
  const helcimClient = useMemo(() => new HelcimClient(grpcBaseUrl), [grpcBaseUrl]);
  const [mode, setMode] = useState<ModalMode>('manage');
  const [activeTab, setActiveTab] = useState<'card' | 'activity'>('card');
  const [selectedTokenId, setSelectedTokenId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Empty submitted query → the server derives a search term from the Kalos user (name/email/phone).
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [pendingRemoveToken, setPendingRemoveToken] = useState('');
  // 'send' (primary) vs 'enter' — the two capture tiles.
  const [captureChoice, setCaptureChoice] = useState<'send' | 'enter'>('send');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recordedLineConsent, setRecordedLineConsent] = useState(false);
  const [confirmingCancelLink, setConfirmingCancelLink] = useState(false);
  // Relink flow: set when the admin enters search via "Search for a different customer" so linking
  // sends relink=true (server neutralizes the old profile instead of blocking). relinkedCustomerName
  // drives the in-panel confirmation banner shown in manage mode after a successful relink.
  const [relinking, setRelinking] = useState(false);
  const [relinkedCustomerName, setRelinkedCustomerName] = useState('');
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  // Attestation (86bbdvb0h): after linking a profile, its unauthorized cards list
  // as checkboxes, all checked; unchecked ones stay saved and unapproved.
  const [attestUnchecked, setAttestUnchecked] = useState<Record<string, boolean>>({});
  const [attestBasis, setAttestBasis] = useState<0 | 2 | 3>(0);
  const consentCheckboxId = useId();

  const { data, isLoading, refetch } = useQuery({
    queryKey: helcimCardsOnFileQueryKey(userId),
    queryFn: async () => {
      const res = await helcimClient.listCardsOnFile(ListCardsOnFileReq.create({ userId }));
      if (!res) throw new Error('Failed to list cards on file');
      return res;
    },
    enabled: open && !!userId && userId > 0,
    staleTime: 30_000,
  });

  const { data: linkStatus } = useQuery({
    queryKey: helcimCardLinkQueryKey(userId),
    queryFn: async () =>
      (await helcimClient.getCardLinkStatus(GetCardLinkStatusReq.create({ userId }))) ?? null,
    enabled: open && !!userId && userId > 0,
    staleTime: 10_000,
  });
  const linkActive = !!linkStatus?.active;

  // The send-link dialog autofills the customer's main email on file (userz.email).
  // Fetched here so every render site gets it — some only have the user id.
  const { data: customerRecord } = useQuery({
    queryKey: ['cardOnFileCustomer', userId] as const,
    queryFn: async () => {
      const userClient = new UserClient(grpcBaseUrl);
      return userClient.Get(User.create({ id: userId }));
    },
    enabled: open && !customerEmail && !!userId && userId > 0,
    staleTime: 5 * 60_000,
  });
  const mainEmail = customerEmail || customerRecord?.email?.trim() || '';

  const cards = useMemo(() => data?.cards ?? [], [data]);
  const primaryTokenId = useMemo(
    () => pickDisplayCard(cards, data?.preferredCardToken)?.tokenId?.trim() ?? '',
    [cards, data?.preferredCardToken],
  );
  // Newest card first (server ordering) — the consent step targets it right after a save.
  const newestCard = cards[0];

  // Pick the initial mode once per open: existing cards or an outstanding link go to manage;
  // otherwise start in capture so the admin can search/link or add the card.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setRelinking(false);
      setRelinkedCustomerName('');
      setConfirmingCancelLink(false);
      setConfirmingUnlink(false);
      setCaptureChoice('send');
      setRecordedLineConsent(false);
      setActiveTab('card');
      return;
    }
    if (isLoading || initializedRef.current) return;
    initializedRef.current = true;
    setMode(cards.length > 0 || linkActive ? 'manage' : 'capture');
  }, [open, isLoading, cards.length, linkActive]);

  useEffect(() => {
    if (!open || isLoading) return;
    setSelectedTokenId((prev) => {
      const trimmed = prev.trim();
      if (trimmed && cards.some((c) => c.tokenId.trim() === trimmed)) return prev;
      return primaryTokenId || cards[0]?.tokenId?.trim() || '';
    });
  }, [open, isLoading, cards, primaryTokenId]);

  const {
    data: searchData,
    isFetching: searchLoading,
    isError: searchError,
  } = useQuery({
    queryKey: ['helcim-profile-search', userId, submittedQuery] as const,
    queryFn: async () => {
      const res = await helcimClient.searchHelcimProfiles(
        SearchHelcimProfilesReq.create({ userId, query: submittedQuery }),
      );
      if (!res) throw new Error('Failed to search Helcim profiles');
      return res;
    },
    enabled: open && (mode === 'search' || mode === 'capture') && !!userId && userId > 0,
    staleTime: 15_000,
  });
  const profiles = searchData?.profiles ?? [];
  const truncated = searchData?.truncated ?? false;

  const selectionDiffersFromPrimary =
    !!selectedTokenId.trim() && !!primaryTokenId && selectedTokenId.trim() !== primaryTokenId;

  const invalidateCardState = async () => {
    await queryClient.invalidateQueries({ queryKey: helcimCardsOnFileQueryKey(userId) });
    await queryClient.invalidateQueries({ queryKey: helcimCardLinkQueryKey(userId) });
  };

  const changeMutation = useMutation({
    mutationFn: async () => {
      if (!onChangeCard) throw new Error('onChangeCard is not set');
      await onChangeCard(selectedTokenId.trim());
    },
    onSuccess: async () => {
      await invalidateCardState();
      await refetch();
      toast({ title: 'Card on file updated' });
      onTokenizeComplete?.();
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not change card',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const linkMutation = useMutation({
    mutationFn: async ({
      helcimCustomerId,
      relink,
    }: {
      helcimCustomerId: number;
      displayName: string;
      relink: boolean;
    }) =>
      helcimClient.linkHelcimProfile(
        LinkHelcimProfileReq.create({ userId, helcimCustomerId, relink }),
      ),
    onSuccess: async (linked, { displayName, relink }) => {
      await invalidateCardState();
      await refetch();
      // Relink shows an in-panel confirmation banner (per mockup); a first-time link keeps the toast.
      if (relink) {
        setRelinkedCustomerName(displayName);
      } else {
        toast({ title: 'Helcim profile linked' });
      }
      onTokenizeComplete?.();
      setRelinking(false);
      // Linking never authorizes. Cards that arrived unauthorized go straight to
      // the attestation step so consent is captured (or a link sent) right now.
      const needsAttest = (linked?.cards ?? []).some((c) => !c.authorized);
      setAttestUnchecked({});
      setAttestBasis(0);
      setMode(needsAttest ? 'attest' : 'manage');
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not link profile',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (cardToken: string) => {
      await helcimClient.removeHelcimCard(RemoveHelcimCardReq.create({ userId, cardToken }));
    },
    onSuccess: async () => {
      setPendingRemoveToken('');
      await invalidateCardState();
      await refetch();
      toast({ title: 'Card removed' });
      onTokenizeComplete?.();
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not remove card',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const sendLinkMutation = useMutation({
    mutationFn: async (email: string) =>
      helcimClient.createCardOnFileLink(CreateCardOnFileLinkReq.create({ userId, email })),
    onSuccess: async (resp) => {
      await invalidateCardState();
      toast({ title: `Card link sent to ${resp.recipientEmail}` });
      setMode('manage');
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not send the link',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const resendLinkMutation = useMutation({
    mutationFn: async () => {
      if (!linkStatus?.linkId) throw new Error('No open link to resend');
      return helcimClient.resendCardOnFileLink(
        ResendCardOnFileLinkReq.create({ linkId: linkStatus.linkId }),
      );
    },
    onSuccess: (resp) => {
      toast({ title: `Link re-sent to ${resp.recipientEmail}` });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not resend the link',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const cancelLinkMutation = useMutation({
    mutationFn: async () => {
      if (!linkStatus?.linkId) throw new Error('No open link to cancel');
      await helcimClient.cancelCardOnFileLink(
        CancelCardOnFileLinkReq.create({ linkId: linkStatus.linkId }),
      );
    },
    onSuccess: async () => {
      // Belt and braces (QA saw the cancel affordance linger): drop the cached
      // link to inactive immediately, THEN refetch — no cache path can keep the
      // banner alive. isPending holds until the refetch resolves.
      queryClient.setQueryData(helcimCardLinkQueryKey(userId), (prev: typeof linkStatus) =>
        prev ? { ...prev, active: false } : prev,
      );
      await invalidateCardState();
      setConfirmingCancelLink(false);
      toast({ title: 'Card link canceled' });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not cancel the link',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const authorizeMutation = useMutation({
    mutationFn: async ({
      cardTokens,
      basisTypeId,
    }: {
      cardTokens: string[];
      basisTypeId: number;
    }) =>
      helcimClient.authorizeCards(AuthorizeCardsReq.create({ userId, cardTokens, basisTypeId })),
    onSuccess: async (_list, { basisTypeId }) => {
      await invalidateCardState();
      await refetch();
      toast({
        title:
          basisTypeId === BASIS_CONFIRMATION_EMAIL
            ? 'Card authorized — confirmation email sent'
            : 'Card authorized on the recorded-line basis',
      });
      setRecordedLineConsent(false);
      setMode('manage');
      onTokenizeComplete?.();
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not authorize the card',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      await helcimClient.unlinkHelcimProfile(UnlinkHelcimProfileReq.create({ userId }));
    },
    onSuccess: async () => {
      setConfirmingUnlink(false);
      await invalidateCardState();
      await refetch();
      toast({ title: 'Helcim profile unlinked' });
      onTokenizeComplete?.();
      setMode('capture');
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not unlink the profile',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const openSendConfirm = () => {
    setRecipientEmail(mainEmail || linkStatus?.recipientEmail || '');
    setMode('sendConfirm');
  };

  // Same shape the customer-edit schema enforces; the consent email must never
  // go to a malformed address (QA finding, 86bav2tff).
  const recipientEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());

  const title = MODAL_TITLES[mode];

  const unauthorizedCards = useMemo(() => cards.filter((c) => !c.authorized), [cards]);
  const attestTokens = unauthorizedCards.map((c) => c.tokenId).filter((t) => !attestUnchecked[t]);

  const backToHome = () => setMode(cards.length > 0 || linkActive ? 'manage' : 'capture');

  // Consent and money flows must not be abandonable mid-flight: while any
  // mutation is in the air the dialog refuses to close (esc, X, outside click
  // all route through onOpenChange).
  const mutationInFlight =
    sendLinkMutation.isPending ||
    resendLinkMutation.isPending ||
    cancelLinkMutation.isPending ||
    authorizeMutation.isPending ||
    linkMutation.isPending ||
    removeMutation.isPending ||
    unlinkMutation.isPending ||
    changeMutation.isPending;

  const handleOpenChange = (next: boolean) => {
    if (!next && mutationInFlight) return;
    if (!next) {
      setPendingRemoveToken('');
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={fullScreenClassName ?? 'max-h-[85vh] max-w-2xl overflow-y-auto bg-white'}
        showClose={!fullScreenClassName}
        // The keyboard re-centring would pull a full-screen surface down off the top.
        keepFocusVisibleAboveKeyboard={!fullScreenClassName}
        onPointerDownOutside={(e) => {
          if (document.getElementById(HELCIM_PAY_IFRAME_ID)) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          {/* pr-8 keeps the row clear of the dialog's own close button; the Card
              Activity control sits right beside it, deliberately unpromoted per the
              PM. Leaving the view unmounts the log, so each visit fetches fresh. */}
          <div
            className={`flex items-center justify-between gap-2 ${fullScreenClassName ? '' : 'pr-8'}`}
          >
            <DialogTitle className={fullScreenClassName ? 'mr-auto' : undefined}>
              {activeTab === 'activity' ? 'Card on File Activity' : title}
            </DialogTitle>
            {activeTab === 'card' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-7 shrink-0 gap-1 px-2 text-xs"
                disabled={mutationInFlight}
                onClick={() => setActiveTab('activity')}
              >
                <History className="h-3.5 w-3.5" />
                Card Activity
              </Button>
            )}
            {fullScreenClassName && (
              <Button
                type="button"
                variant="ghost"
                className="-mr-3 shrink-0"
                disabled={mutationInFlight}
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            )}
          </div>
        </DialogHeader>

        {activeTab === 'activity' && (
          <div className="grid gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-mt-2 w-fit gap-1 px-2"
              onClick={() => setActiveTab('card')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to cards on file
            </Button>
            <CardOnFileAuditLog userId={userId} grpcBaseUrl={grpcBaseUrl} />
          </div>
        )}

        {activeTab === 'card' && (
          <div className="grid gap-4">
            {mode === 'capture' && (
              <>
                {(cards.length > 0 || linkActive) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-mt-2 mb-2 w-fit gap-1 px-2"
                    disabled={mutationInFlight}
                    onClick={() => setMode('manage')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                )}
                <p className="text-muted-foreground text-sm">
                  If this customer already exists in Helcim, search and link their profile.
                  Otherwise add the card below.
                </p>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    // The modal is portaled (Radix), but React synthetic events still bubble up the
                    // React tree to any enclosing <form> (e.g. the job-creation react-hook-form), which
                    // would submit/create the job. stopPropagation keeps the search submit local.
                    e.preventDefault();
                    e.stopPropagation();
                    setSubmittedQuery(searchInput.trim());
                  }}
                >
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by name, email, or phone"
                    aria-label="Search Helcim profiles"
                  />
                  <Button type="submit" variant="outline" disabled={searchLoading}>
                    {searchLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="sr-only">Search</span>
                  </Button>
                </form>
                <div className="max-h-52 space-y-2 overflow-y-auto">
                  {searchLoading && (
                    <p className="text-muted-foreground text-sm">Searching Helcim profiles…</p>
                  )}
                  {!searchLoading && searchError && (
                    <p className="text-destructive text-sm">Search failed. Try again.</p>
                  )}
                  {!searchLoading && !searchError && profiles.length === 0 && (
                    <div>
                      <p className="text-sm font-medium text-amber-700">
                        No matching Helcim profiles
                      </p>
                      <p className="text-muted-foreground text-xs">
                        Try phone or email — profiles created from the generic payment link often
                        don&apos;t match names.
                      </p>
                    </div>
                  )}
                  {profiles.map((p) => (
                    <ProfileResultRow
                      key={p.helcimCustomerId}
                      profile={p}
                      linking={linkMutation.isPending}
                      onLink={(displayName) =>
                        linkMutation.mutate({
                          helcimCustomerId: p.helcimCustomerId,
                          displayName,
                          relink: relinking,
                        })
                      }
                    />
                  ))}
                  {truncated && (
                    <p className="text-muted-foreground text-xs">
                      Showing the first {profiles.length} matches — refine your search.
                    </p>
                  )}
                </div>

                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                  Add the card
                </p>
                <p className="text-muted-foreground -mt-2 text-xs">
                  A Helcim profile is created and linked automatically if none exists.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <CaptureTile
                    selected={captureChoice === 'send'}
                    disabled={mutationInFlight}
                    title="Send link to customer"
                    description={
                      mainEmail
                        ? `Emails ${mainEmail} a secure page to add and approve their card`
                        : 'Emails the customer a secure page to add and approve their card'
                    }
                    onSelect={() => setCaptureChoice('send')}
                  />
                  <CaptureTile
                    selected={captureChoice === 'enter'}
                    disabled={mutationInFlight}
                    title="Enter card now"
                    description="Customer on the phone reads the card. Consent is recorded after saving"
                    onSelect={() => setCaptureChoice('enter')}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={mutationInFlight}
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={mutationInFlight}
                    onClick={() =>
                      captureChoice === 'send' ? openSendConfirm() : setMode('enterNow')
                    }
                  >
                    {captureChoice === 'send' ? (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send card-on-file link
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Open card entry
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}

            {mode === 'sendConfirm' && (
              <>
                <p className="text-muted-foreground text-sm">
                  Email a secure card link to this address. They add and approve their card on the
                  page. Nothing is chargeable until they finish.
                </p>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="customer@email.com"
                  aria-label="Recipient email"
                  aria-invalid={!!recipientEmail.trim() && !recipientEmailValid}
                  disabled={sendLinkMutation.isPending}
                />
                {!!recipientEmail.trim() && !recipientEmailValid && (
                  <p className="text-destructive -mt-2 text-xs">Enter a valid email address.</p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={sendLinkMutation.isPending}
                    onClick={backToHome}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={!recipientEmailValid || sendLinkMutation.isPending}
                    onClick={() => sendLinkMutation.mutate(recipientEmail.trim())}
                  >
                    {sendLinkMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Send link'
                    )}
                  </Button>
                </div>
              </>
            )}

            {mode === 'enterNow' && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-mt-2 mb-2 w-fit gap-1 px-2"
                  onClick={() => setMode('capture')}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <HelcimCardTokenizePanel
                  userId={userId}
                  grpcBaseUrl={grpcBaseUrl}
                  onCancel={() => setMode('capture')}
                  onDone={() => {
                    void (async () => {
                      // Server-side save completion: the newest card becomes the
                      // preferred token and the card_saved audit entry is written.
                      try {
                        await helcimClient.completeCardSave(CompleteCardSaveReq.create({ userId }));
                      } catch {
                        // Best-effort; consent capture continues regardless.
                      }
                      await invalidateCardState();
                      await refetch();
                      setMode('consent');
                    })();
                    onTokenizeComplete?.();
                  }}
                />
              </>
            )}

            {mode === 'attest' && (
              <>
                <p className="text-muted-foreground text-sm">
                  All are saved. Uncheck any the customer did not ask for — those stay saved and
                  unapproved.
                </p>
                <div className="space-y-2">
                  {unauthorizedCards.map((c) => (
                    <label
                      key={c.tokenId}
                      className="border-border flex items-center gap-2 rounded-md border bg-white px-3 py-2"
                    >
                      <Checkbox
                        checked={!attestUnchecked[c.tokenId]}
                        disabled={authorizeMutation.isPending}
                        onCheckedChange={(v) =>
                          setAttestUnchecked((prev) => ({ ...prev, [c.tokenId]: v !== true }))
                        }
                      />
                      <span className="text-sm font-medium">
                        {formatBrandLabel(c.brand)} •••• {c.last4}
                      </span>
                    </label>
                  ))}
                  {unauthorizedCards.length === 0 && (
                    <p className="text-muted-foreground text-sm">
                      Every saved card is already authorized.
                    </p>
                  )}
                </div>
                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                  How did the customer give consent?
                </p>
                <div className="space-y-2">
                  <CaptureTile
                    selected={attestBasis === BASIS_CONFIRMATION_EMAIL}
                    disabled={authorizeMutation.isPending}
                    title="Asked us on the phone now or in writing"
                    description="Sends a confirmation email listing each approved card — the email is the record"
                    onSelect={() => setAttestBasis(BASIS_CONFIRMATION_EMAIL)}
                  />
                  <CaptureTile
                    selected={attestBasis === BASIS_RECORDED_LINE}
                    disabled={authorizeMutation.isPending}
                    title="Verbal consent on a recorded line (COF)"
                    description="No email. The recorded call is the consent record"
                    onSelect={() => setAttestBasis(BASIS_RECORDED_LINE)}
                  />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={mutationInFlight}
                    onClick={openSendConfirm}
                  >
                    Send a link instead
                  </Button>
                  <Button
                    type="button"
                    disabled={
                      attestBasis === 0 || attestTokens.length === 0 || authorizeMutation.isPending
                    }
                    onClick={() =>
                      authorizeMutation.mutate({
                        cardTokens: attestTokens,
                        basisTypeId: attestBasis,
                      })
                    }
                  >
                    {authorizeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Authorizing…
                      </>
                    ) : (
                      `Authorize ${attestTokens.length} card${attestTokens.length === 1 ? '' : 's'}${
                        attestBasis === BASIS_CONFIRMATION_EMAIL ? ' and send email' : ''
                      }`
                    )}
                  </Button>
                </div>
              </>
            )}

            {mode === 'consent' && (
              <>
                <p className="text-muted-foreground text-sm">
                  The card is saved
                  {newestCard
                    ? ` (${formatBrandLabel(newestCard.brand)} •••• ${newestCard.last4})`
                    : ''}{' '}
                  but not yet chargeable. Record how the customer consented.
                </p>
                <div className="flex items-start gap-2 rounded-md border px-3 py-2">
                  <Checkbox
                    id={consentCheckboxId}
                    checked={recordedLineConsent}
                    disabled={authorizeMutation.isPending}
                    onCheckedChange={(v) => setRecordedLineConsent(v === true)}
                  />
                  <Label htmlFor={consentCheckboxId} className="text-sm font-normal">
                    This was verbal consent on a recorded line
                    <span className="text-muted-foreground block text-xs">
                      No email is sent — the recorded call is the consent record.
                    </span>
                  </Label>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={authorizeMutation.isPending}
                    onClick={() => {
                      // Closing leaves the card saved and unapproved.
                      setRecordedLineConsent(false);
                      setMode('manage');
                    }}
                  >
                    Close without authorizing
                  </Button>
                  <Button
                    type="button"
                    disabled={!newestCard || authorizeMutation.isPending}
                    onClick={() =>
                      authorizeMutation.mutate({
                        cardTokens: [newestCard?.tokenId ?? ''],
                        basisTypeId: recordedLineConsent
                          ? BASIS_RECORDED_LINE
                          : BASIS_CONFIRMATION_EMAIL,
                      })
                    }
                  >
                    {authorizeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Authorizing…
                      </>
                    ) : recordedLineConsent ? (
                      'Authorize card'
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Send confirmation email
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}

            {mode === 'search' && (
              <>
                {relinking && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-mt-2 mb-2 w-fit gap-1 px-2"
                    onClick={() => {
                      setRelinking(false);
                      setMode('manage');
                    }}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Cards on file
                  </Button>
                )}
                <p className="text-muted-foreground text-sm">
                  Search Helcim for this customer&apos;s existing profile and link it, or create a
                  new profile from their Kalos info.
                </p>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSubmittedQuery(searchInput.trim());
                  }}
                >
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by name, email, or phone"
                    aria-label="Search Helcim profiles"
                  />
                  <Button type="submit" variant="outline" disabled={searchLoading}>
                    {searchLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="sr-only">Search</span>
                  </Button>
                </form>

                <div className="max-h-80 space-y-2 overflow-y-auto">
                  {searchLoading && (
                    <p className="text-muted-foreground text-sm">Searching Helcim profiles…</p>
                  )}
                  {!searchLoading && searchError && (
                    <p className="text-destructive text-sm">Search failed. Try again.</p>
                  )}
                  {!searchLoading && !searchError && profiles.length === 0 && (
                    <p className="text-muted-foreground text-sm">
                      No matching Helcim profiles. Create a new one below.
                    </p>
                  )}
                  {profiles.map((p) => (
                    <ProfileResultRow
                      key={p.helcimCustomerId}
                      profile={p}
                      linking={linkMutation.isPending}
                      onLink={(displayName) =>
                        linkMutation.mutate({
                          helcimCustomerId: p.helcimCustomerId,
                          displayName,
                          relink: relinking,
                        })
                      }
                    />
                  ))}
                </div>
                {truncated && (
                  <p className="text-muted-foreground text-xs">
                    Showing the first {profiles.length} matches — refine your search to narrow it
                    down.
                  </p>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={mutationInFlight}
                  onClick={() => setMode('enterNow')}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create new profile from customer info
                </Button>
              </>
            )}

            {mode === 'manage' && (
              <>
                {relinkedCustomerName && (
                  <div className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
                    Relinked to {relinkedCustomerName}. Original card and Helcim profile were not
                    touched.
                  </div>
                )}

                {linkActive && (
                  <div
                    className={`rounded-md border border-blue-300 bg-blue-50 px-3 py-2 ${
                      cancelLinkMutation.isPending ? 'opacity-70' : ''
                    }`}
                    aria-busy={cancelLinkMutation.isPending}
                  >
                    <p className="text-sm font-medium text-blue-900">
                      {linkStatus?.completed ? 'Link used' : 'Link sent'} ·{' '}
                      {linkStatus?.recipientEmail}
                    </p>
                    <p className="mt-0.5 text-xs text-blue-800">
                      {linkStatus?.completed
                        ? 'The customer responded through this link. It stays live if they need it again.'
                        : 'Waiting for the customer to approve their card. Updates automatically.'}
                    </p>
                    {cancelLinkMutation.isPending ? (
                      <div className="mt-2 flex items-center gap-2 text-xs text-blue-900">
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        Canceling link…
                      </div>
                    ) : confirmingCancelLink ? (
                      <div className="mt-2 flex items-center gap-2">
                        <p className="text-xs text-blue-900">
                          Cancel this link? The page stops working.
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="h-7 px-2 text-xs"
                          disabled={mutationInFlight}
                          onClick={() => cancelLinkMutation.mutate()}
                        >
                          Cancel link
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          disabled={mutationInFlight}
                          onClick={() => setConfirmingCancelLink(false)}
                        >
                          Keep it
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-2 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 bg-white px-2 text-xs"
                          disabled={mutationInFlight}
                          onClick={() => resendLinkMutation.mutate()}
                        >
                          {resendLinkMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            'Resend link'
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          disabled={mutationInFlight}
                          onClick={() => setConfirmingCancelLink(true)}
                        >
                          Cancel link
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-muted-foreground text-sm">
                  Select which saved card to use, then select{' '}
                  <span className="font-medium">Change card</span>.
                </p>
                <div className="space-y-2" role="radiogroup" aria-label="Saved cards">
                  {isLoading && (
                    <p className="text-muted-foreground text-sm">Loading saved cards…</p>
                  )}
                  {!isLoading && cards.length === 0 && (
                    <p className="text-muted-foreground text-sm">No cards on file yet.</p>
                  )}
                  {cards.map((c) => (
                    <SelectableCardRow
                      key={c.tokenId || `${c.last4}-${c.expMonth}-${c.expYear}`}
                      card={c}
                      selected={c.tokenId.trim() === selectedTokenId.trim()}
                      isPrimary={!!primaryTokenId && c.tokenId.trim() === primaryTokenId}
                      disabled={mutationInFlight}
                      onSelect={() => setSelectedTokenId(c.tokenId)}
                      confirmingRemove={!!c.tokenId && pendingRemoveToken === c.tokenId}
                      removing={removeMutation.isPending && pendingRemoveToken === c.tokenId}
                      onRequestRemove={() => setPendingRemoveToken(c.tokenId)}
                      onCancelRemove={() => setPendingRemoveToken('')}
                      onConfirmRemove={() => removeMutation.mutate(c.tokenId)}
                      onSendAuthLink={openSendConfirm}
                      onAuthorize={() => {
                        // Manual consent for an already-saved card (QA, 86bbdvay3):
                        // same attest step the post-link path uses, all unauthorized
                        // cards checked.
                        setAttestUnchecked({});
                        setAttestBasis(0);
                        setMode('attest');
                      }}
                    />
                  ))}
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  {onChangeCard ? (
                    <Button
                      type="button"
                      className="w-full"
                      disabled={
                        !selectedTokenId.trim() || !selectionDiffersFromPrimary || mutationInFlight
                      }
                      onClick={() => changeMutation.mutate()}
                    >
                      {changeMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Updating…
                        </>
                      ) : (
                        'Change card'
                      )}
                    </Button>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="w-full">
                            <Button type="button" className="w-full" disabled>
                              Change card
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>
                            Changing the default card will be available once this screen is wired to
                            your API.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={mutationInFlight}
                    onClick={() => setMode('capture')}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Add card on file
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    disabled={mutationInFlight}
                    onClick={() => {
                      setRelinkedCustomerName('');
                      setRelinking(true);
                      setMode('search');
                    }}
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Search for a different customer
                  </Button>
                  {confirmingUnlink ? (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                      <p className="text-sm text-red-900">
                        Disconnect this Helcim profile
                        {cards.length > 0
                          ? ` and its ${cards.length} card${cards.length === 1 ? '' : 's'}`
                          : ''}{' '}
                        from this customer? Nothing is deleted in Helcim, but the cards can no
                        longer be charged and any approvals are removed.
                      </p>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={mutationInFlight}
                          onClick={() => setConfirmingUnlink(false)}
                        >
                          Keep it linked
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          disabled={mutationInFlight}
                          onClick={() => unlinkMutation.mutate()}
                        >
                          {unlinkMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Unlink profile'
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-destructive hover:text-destructive w-full"
                      disabled={mutationInFlight}
                      onClick={() => setConfirmingUnlink(true)}
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      Unlink Helcim profile
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

function CaptureTile({
  selected,
  disabled = false,
  title,
  description,
  onSelect,
}: {
  selected: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`rounded-md border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? 'border-primary bg-primary/5 ring-primary ring-1'
          : 'border-border bg-white hover:bg-gray-50'
      }`}
    >
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>
    </button>
  );
}

function ProfileResultRow({
  profile,
  onLink,
  linking,
}: {
  profile: HelcimProfile;
  onLink: (displayName: string) => void;
  linking: boolean;
}) {
  const otherUser = profile.linkState === ProfileLinkState.PROFILE_LINK_OTHER_USER;
  const thisUser = profile.linkState === ProfileLinkState.PROFILE_LINK_THIS_USER;
  const name =
    profile.contactName.trim() ||
    profile.businessName.trim() ||
    `Helcim customer #${profile.helcimCustomerId}`;
  const contactLine = [profile.email.trim(), profile.phone.trim()].filter(Boolean).join(' · ');

  return (
    <div className="border-border rounded-md border bg-white px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{name}</p>
          {!!profile.businessName.trim() && profile.businessName.trim() !== name && (
            <p className="text-muted-foreground truncate text-xs">{profile.businessName}</p>
          )}
          <p className="text-muted-foreground truncate text-xs">{contactLine || '—'}</p>
        </div>
        <div className="shrink-0">
          {otherUser ? (
            <Badge variant="outline" className="border-amber-300 text-amber-700">
              Linked to another customer
            </Badge>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={linking || thisUser}
              onClick={() => onLink(name)}
            >
              {thisUser ? 'Already linked' : 'Link'}
            </Button>
          )}
        </div>
      </div>
      {profile.cards.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {profile.cards.map((c) => (
            <Badge key={c.tokenId} variant="secondary" className="text-xs">
              {formatBrandLabel(c.brand)} •••• {c.last4}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground mt-2 text-xs">No cards on this profile</p>
      )}
    </div>
  );
}

function SelectableCardRow({
  card,
  selected,
  isPrimary,
  disabled,
  onSelect,
  confirmingRemove,
  removing,
  onRequestRemove,
  onCancelRemove,
  onConfirmRemove,
  onSendAuthLink,
  onAuthorize,
}: {
  card: HelcimCard;
  selected: boolean;
  isPrimary: boolean;
  disabled: boolean;
  onSelect: () => void;
  confirmingRemove: boolean;
  removing: boolean;
  onRequestRemove: () => void;
  onCancelRemove: () => void;
  onConfirmRemove: () => void;
  onSendAuthLink: () => void;
  onAuthorize: () => void;
}) {
  const exp =
    card.expMonth > 0 && card.expYear > 0
      ? `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`
      : '—';
  return (
    <div
      className={`border-border overflow-hidden rounded-md border bg-white ${
        selected ? 'ring-primary ring-2 ring-offset-2' : ''
      }`}
    >
      <div className="flex items-stretch">
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          disabled={disabled}
          onClick={onSelect}
          className="min-w-0 flex-1 px-3 py-2 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {formatBrandLabel(card.brand)} •••• {card.last4}
              </p>
              <p className="text-muted-foreground text-xs">Exp {exp}</p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
              {isPrimary && (
                <Badge variant="secondary" className="text-xs">
                  Current
                </Badge>
              )}
              {card.authorized ? (
                <Badge variant="outline" className="border-emerald-300 text-xs text-emerald-700">
                  Authorized
                </Badge>
              ) : (
                <Badge variant="outline" className="border-amber-300 text-xs text-amber-700">
                  Needs authorization
                </Badge>
              )}
              <Badge variant="outline" className="text-xs">
                {cardStatusLabel(card.status)}
              </Badge>
            </div>
          </div>
          {!card.authorized && (
            <p className="text-muted-foreground mt-1 text-xs">
              Needs customer approval before it can be charged.
            </p>
          )}
        </button>
        {!!card.tokenId && !confirmingRemove && (
          <button
            type="button"
            disabled={disabled}
            onClick={onRequestRemove}
            aria-label="Remove card"
            className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive border-border flex items-center border-l px-3 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {!card.authorized && !confirmingRemove && (
        <div className="flex flex-wrap gap-2 border-t px-3 py-1.5">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            disabled={disabled}
            onClick={onAuthorize}
          >
            <ShieldCheck className="mr-1.5 h-3 w-3" />
            Authorize
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            disabled={disabled}
            onClick={onSendAuthLink}
          >
            <Send className="mr-1.5 h-3 w-3" />
            Send authorization link
          </Button>
        </div>
      )}
      {confirmingRemove && (
        <div className="bg-destructive/5 flex flex-col gap-2 border-t px-3 py-2">
          <p className="text-sm">
            Remove this card? It&apos;ll be permanently deleted from Helcim and can&apos;t be
            recovered. If the card is fine but just linked to the wrong customer, use{' '}
            <span className="font-medium">Search for a different customer</span> instead.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancelRemove}
              disabled={disabled || removing}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onConfirmRemove}
              disabled={disabled || removing}
            >
              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
